import { useMemo } from 'react'
import { GanttDisplayUnit } from '../../../../../../../../../../domain/value-object/GanttParameterVO'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'

export interface DatePosition {
  x: number // percentage (0-100)
  width: number // percentage
  date: CalendarDateVO
}

interface UseDatePositionsParams {
  timeScale: CalendarDateVO[]
  unit: GanttDisplayUnit
}

/**
 * Calculate date positions (x coordinates as percentages) for Gantt chart
 */
export function useDatePositions({
  timeScale,
  unit,
}: UseDatePositionsParams): DatePosition[] {
  // Calculate month widths for MONTH display unit
  const monthWidthInfo = useMemo(() => {
    const uniqueMonths = new Set(
      timeScale.map(
        date => `${date.date.getFullYear()}-${date.date.getMonth()}`
      )
    )
    const monthWidth = 100 / uniqueMonths.size

    // Cache month days
    const monthDaysMap = new Map<string, number>()
    timeScale.forEach(date => {
      const key = `${date.date.getFullYear()}-${date.date.getMonth()}`
      if (!monthDaysMap.has(key)) {
        const lastDay = new Date(
          date.date.getFullYear(),
          date.date.getMonth() + 1,
          0
        ).getDate()
        monthDaysMap.set(key, lastDay)
      }
    })

    return { monthWidth, monthDaysMap }
  }, [timeScale])

  // Calculate date positions
  const datePositions = useMemo(() => {
    const positions: DatePosition[] = []
    let currentX = 0

    timeScale.forEach(date => {
      let width: number
      if (unit === GanttDisplayUnit.MONTH) {
        const key = `${date.date.getFullYear()}-${date.date.getMonth()}`
        const monthDays = monthWidthInfo.monthDaysMap.get(key) || 30
        width = monthWidthInfo.monthWidth / monthDays
      } else {
        width = 100 / timeScale.length
      }

      positions.push({ x: currentX, width, date })
      currentX += width
    })

    return positions
  }, [timeScale, unit, monthWidthInfo])

  return datePositions
}

/**
 * Helper to find pixel X coordinate for a CalendarDateVO
 */
export function getPixelXForDate(
  datePositions: DatePosition[],
  targetDate: CalendarDateVO,
  containerWidth: number,
  edge: 'left' | 'right' | 'center' = 'center'
): number | null {
  const pos = datePositions.find(p => p.date.isEqual(targetDate))
  if (!pos) return null

  const pixelX = (pos.x / 100) * containerWidth
  const pixelWidth = (pos.width / 100) * containerWidth

  switch (edge) {
    case 'left':
      return pixelX
    case 'right':
      return pixelX + pixelWidth
    case 'center':
    default:
      return pixelX + pixelWidth / 2
  }
}

/**
 * Reverse lookup: percentage X coordinate → CalendarDateVO
 * Finds which date corresponds to a given percentage X position.
 */
export function getDateForPercentX(
  datePositions: DatePosition[],
  percentX: number
): CalendarDateVO | null {
  for (const pos of datePositions) {
    if (percentX >= pos.x && percentX < pos.x + pos.width) {
      return pos.date
    }
  }
  // Check last position (inclusive of right edge)
  if (datePositions.length > 0) {
    const last = datePositions[datePositions.length - 1]
    if (Math.abs(percentX - (last.x + last.width)) < 0.001) {
      return last.date
    }
  }
  return null
}

/**
 * Helper to find percentage X coordinate for a CalendarDateVO (for SVG)
 */
export function getPercentXForDate(
  datePositions: DatePosition[],
  targetDate: CalendarDateVO,
  edge: 'left' | 'right' | 'center' = 'center'
): number | null {
  const pos = datePositions.find(p => p.date.isEqual(targetDate))
  if (!pos) return null

  switch (edge) {
    case 'left':
      return pos.x
    case 'right':
      return pos.x + pos.width
    case 'center':
    default:
      return pos.x + pos.width / 2
  }
}
